import { useRef, useEffect, useState, useCallback } from 'react'
import { IconClose } from './Icons'
import { useModalA11y } from '../utils/useModalA11y'
import {
  roughRect,
  roughDiamondInRect,
  roughEllipseInRect,
  roughArrow,
  nodeCenter,
  nodeHalfDiag,
  shortenSegment,
  findNodeAtPoint,
} from '../utils/roughFlowDraw'
import './FlowEditorModal.css'

const SIZES = [
  { w: 800, h: 560, label: '800×560' },
  { w: 900, h: 640, label: '900×640' },
  { w: 1024, h: 720, label: '1024×720' },
]

const SHAPE_DEFAULTS = {
  rect: { w: 168, h: 72 },
  diamond: { w: 128, h: 112 },
  ellipse: { w: 148, h: 76 },
}

function newNode(type, cx, cy) {
  const def = SHAPE_DEFAULTS[type]
  const text =
    type === 'ellipse' ? 'התחלה' : type === 'diamond' ? 'החלטה?' : 'צעד'
  return {
    id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type,
    x: cx - def.w / 2,
    y: cy - def.h / 2,
    w: def.w,
    h: def.h,
    text,
    seed: (Math.random() * 0xffffffff) >>> 0,
  }
}

function edgeSeed(from, to) {
  const s = `${from}>${to}`
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h >>> 0
}

function drawFlow(ctx, nodes, edges, { selectedId, connectFromId, accent }) {
  const { width: cw, height: ch } = ctx.canvas
  ctx.save()
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, cw, ch)

  for (const e of edges) {
    const a = nodes.find((n) => n.id === e.from)
    const b = nodes.find((n) => n.id === e.to)
    if (!a || !b) continue
    const c1 = nodeCenter(a)
    const c2 = nodeCenter(b)
    const d1 = nodeHalfDiag(a) * 0.72
    const d2 = nodeHalfDiag(b) * 0.72
    const s = shortenSegment(c1.x, c1.y, c2.x, c2.y, d1, d2)
    roughArrow(ctx, s.x1, s.y1, s.x2, s.y2, e.seed ?? edgeSeed(e.from, e.to), 2.05)
  }

  for (const n of nodes) {
    const seed = n.seed ?? 1
    if (n.type === 'rect') roughRect(ctx, n.x, n.y, n.w, n.h, seed, 2.05)
    else if (n.type === 'diamond') roughDiamondInRect(ctx, n.x, n.y, n.w, n.h, seed, 2.05)
    else roughEllipseInRect(ctx, n.x, n.y, n.w, n.h, seed, 2.05)

    ctx.save()
    ctx.fillStyle = '#141414'
    ctx.font = '600 14px Heebo, system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const c = nodeCenter(n)
    const raw = (n.text || '').trim() || '…'
    const lines = raw.split(/\n/).slice(0, 4)
    const lineH = 17
    const startY = c.y - ((lines.length - 1) * lineH) / 2
    lines.forEach((line, i) => {
      ctx.fillText(line.slice(0, 42), c.x, startY + i * lineH)
    })
    ctx.restore()
  }

  if (selectedId) {
    const n = nodes.find((x) => x.id === selectedId)
    if (n) {
      const pad = 6
      ctx.save()
      ctx.strokeStyle = accent || '#2563eb'
      ctx.lineWidth = 2
      ctx.setLineDash([6, 4])
      ctx.strokeRect(n.x - pad, n.y - pad, n.w + pad * 2, n.h + pad * 2)
      ctx.restore()
    }
  }

  if (connectFromId) {
    const n = nodes.find((x) => x.id === connectFromId)
    if (n) {
      const c = nodeCenter(n)
      ctx.save()
      ctx.fillStyle = 'rgba(37, 99, 235, 0.25)'
      ctx.beginPath()
      ctx.arc(c.x, c.y, 10, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }
  }

  ctx.restore()
}

export function FlowEditorModal({ onCreate, onClose, lang = 'he' }) {
  const t = (en, he) => (lang === 'en' ? en : he)
  const [sizeIdx, setSizeIdx] = useState(1)
  const size = SIZES[sizeIdx]
  const [nodes, setNodes] = useState([])
  const [edges, setEdges] = useState([])
  const [mode, setMode] = useState('select') // select | rect | diamond | ellipse | connect
  const [selectedId, setSelectedId] = useState(null)
  const [connectFrom, setConnectFrom] = useState(null)
  const canvasRef = useRef(null)
  const panelRef = useRef(null)
  const dragRef = useRef(null)

  useModalA11y({ onClose, modalRef: panelRef })

  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    drawFlow(ctx, nodes, edges, {
      selectedId,
      connectFromId: connectFrom,
      accent: '#2563eb',
    })
  }, [nodes, edges, selectedId, connectFrom])

  useEffect(() => {
    redraw()
  }, [redraw])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = size.w
    canvas.height = size.h
    redraw()
  }, [size.w, size.h, redraw])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setConnectFrom(null)
        return
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!selectedId) return
        if (e.target.closest?.('input, textarea, select')) return
        e.preventDefault()
        setEdges((prev) => prev.filter((x) => x.from !== selectedId && x.to !== selectedId))
        setNodes((prev) => prev.filter((x) => x.id !== selectedId))
        setSelectedId(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedId])

  const canvasCoords = (e) => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const sx = canvas.width / rect.width
    const sy = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * sx,
      y: (e.clientY - rect.top) * sy,
    }
  }

  const clampNode = useCallback((n) => {
    const m = 8
    let { x, y, w, h } = n
    x = Math.max(m, Math.min(size.w - w - m, x))
    y = Math.max(m, Math.min(size.h - h - m, y))
    return { ...n, x, y }
  }, [size.w, size.h])

  const onPointerDown = (e) => {
    const p = canvasCoords(e)
    if (!p) return
    const hit = findNodeAtPoint(nodes, p.x, p.y)

    if (mode === 'connect') {
      if (!hit) return
      if (!connectFrom) {
        setConnectFrom(hit.id)
        setSelectedId(hit.id)
        return
      }
      if (connectFrom === hit.id) {
        setConnectFrom(null)
        return
      }
      const from = connectFrom
      const to = hit.id
      setEdges((prev) => {
        if (prev.some((x) => x.from === from && x.to === to)) return prev
        return [...prev, { id: `e-${from}-${to}`, from, to, seed: edgeSeed(from, to) }]
      })
      setConnectFrom(null)
      setSelectedId(hit.id)
      return
    }

    if (mode === 'select') {
      if (hit) {
        dragRef.current = { id: hit.id, sx: p.x - hit.x, sy: p.y - hit.y }
        setSelectedId(hit.id)
      } else {
        setSelectedId(null)
        setConnectFrom(null)
      }
      return
    }

    if (mode === 'rect' || mode === 'diamond' || mode === 'ellipse') {
      if (hit) {
        dragRef.current = { id: hit.id, sx: p.x - hit.x, sy: p.y - hit.y }
        setSelectedId(hit.id)
        setMode('select')
        return
      }
      const nn = newNode(mode, p.x, p.y)
      setNodes((prev) => [...prev, clampNode(nn)])
      setSelectedId(nn.id)
      setMode('select')
    }
  }

  const onPointerMove = (e) => {
    const d = dragRef.current
    if (!d) return
    const p = canvasCoords(e)
    if (!p) return
    setNodes((prev) =>
      prev.map((n) =>
        n.id === d.id ? clampNode({ ...n, x: p.x - d.sx, y: p.y - d.sy }) : n,
      ),
    )
  }

  const onPointerUp = () => {
    dragRef.current = null
  }

  const onDoubleClick = (e) => {
    const p = canvasCoords(e)
    if (!p) return
    const hit = findNodeAtPoint(nodes, p.x, p.y)
    if (!hit) return
    const next = window.prompt(t('Node text', 'טקסט בתוך הצורה'), hit.text || '')
    if (next == null) return
    setNodes((prev) => prev.map((n) => (n.id === hit.id ? { ...n, text: next } : n)))
  }

  const clearAll = () => {
    setNodes([])
    setEdges([])
    setSelectedId(null)
    setConnectFrom(null)
  }

  const deleteSelected = () => {
    if (!selectedId) return
    setEdges((prev) => prev.filter((x) => x.from !== selectedId && x.to !== selectedId))
    setNodes((prev) => prev.filter((x) => x.id !== selectedId))
    setSelectedId(null)
  }

  const applyToPage = () => {
    const canvas = document.createElement('canvas')
    canvas.width = size.w
    canvas.height = size.h
    const ctx = canvas.getContext('2d')
    drawFlow(ctx, nodes, edges, { selectedId: null, connectFromId: null })
    onCreate?.(canvas)
    onClose?.()
  }

  return (
    <div className="flow-editor-modal" role="dialog" aria-modal="true" aria-label={t('Flow diagram', 'תרשים זרימה')}>
      <div className="flow-editor-backdrop" onClick={onClose} aria-hidden />
      <div className="flow-editor-shell" ref={panelRef} tabIndex={-1}>
        <div className="flow-editor-header">
          <h2>{t('Flow sketch (Excalidraw-style)', 'תרשים זרימה — סגנון Excalidraw')}</h2>
          <button type="button" className="flow-editor-close" onClick={onClose} aria-label={t('Close', 'סגור')}>
            <IconClose size={22} />
          </button>
        </div>

        <div className="flow-editor-toolbar">
          <button type="button" className={`flow-editor-tool-btn ${mode === 'select' ? 'active' : ''}`} onClick={() => { setMode('select'); setConnectFrom(null); }}>
            {t('Select', 'בחירה')}
          </button>
          <button type="button" className={`flow-editor-tool-btn ${mode === 'rect' ? 'active' : ''}`} onClick={() => { setMode('rect'); setConnectFrom(null); }}>
            {t('Rectangle', 'מלבן')}
          </button>
          <button type="button" className={`flow-editor-tool-btn ${mode === 'diamond' ? 'active' : ''}`} onClick={() => { setMode('diamond'); setConnectFrom(null); }}>
            {t('Diamond', 'מעוין')}
          </button>
          <button type="button" className={`flow-editor-tool-btn ${mode === 'ellipse' ? 'active' : ''}`} onClick={() => { setMode('ellipse'); setConnectFrom(null); }}>
            {t('Ellipse', 'אליפסה')}
          </button>
          <button type="button" className={`flow-editor-tool-btn ${mode === 'connect' ? 'active' : ''}`} onClick={() => { setMode('connect'); setConnectFrom(null); }}>
            {t('Arrow', 'חץ')}
          </button>
          <button type="button" className="flow-editor-tool-btn danger" onClick={deleteSelected} disabled={!selectedId}>
            {t('Delete', 'מחק')}
          </button>
          <button type="button" className="flow-editor-tool-btn danger" onClick={clearAll} disabled={nodes.length === 0}>
            {t('Clear all', 'נקה הכל')}
          </button>
          <div className="flow-editor-meta">
            <span>{t('Canvas', 'לוח')}</span>
            <select value={String(sizeIdx)} onChange={(e) => setSizeIdx(Number(e.target.value))} aria-label={t('Canvas size', 'גודל לוח')}>
              {SIZES.map((s, i) => (
                <option key={s.label} value={String(i)}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flow-editor-body">
          <div className="flow-editor-canvas-wrap">
            <canvas
              ref={canvasRef}
              className="flow-editor-canvas"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
              onDoubleClick={onDoubleClick}
            />
          </div>
          <p className="flow-editor-hint">
            {mode === 'connect'
              ? t('Tap two shapes to connect with an arrow.', 'לחצו על שני צורות ברצף כדי לחבר בחץ.')
              : t('Double-click a shape to edit text. Drag to move. Delete key removes selection.', 'לחיצה כפולה לעריכת טקסט. גרירה להזזה. מקש מחיקה מסיר את הנבחר.')}
          </p>
        </div>

        <div className="flow-editor-actions">
          <button type="button" onClick={onClose}>
            {t('Cancel', 'ביטול')}
          </button>
          <button type="button" className="primary" onClick={applyToPage} disabled={nodes.length === 0}>
            {t('Use as coloring page', 'הפוך לדף צביעה')}
          </button>
        </div>
      </div>
    </div>
  )
}
